import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { isEqual } from 'es-toolkit/compat';
import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MarginDetails } from '../models/margin-details.model';
import { AdditionalRatesStore } from './additional-rates/additional-rates.store';
import { CandidateDetailsStore } from './candidate-details/candidate-details.store';
import { JobDetailsStore } from './job-details/job-details.store';
import { LocumsMarginCalculatorSlice, initialLocumsMarginCalculatorSlice } from './locums-margin-calculator.slice';
import { MarginCalculationsStore } from './margin-calculations/margin-calculations.store';
import { MarginContractStore } from './margin-contract/margin-contract.store';
import { MarginExpensesStore } from './margin-expenses/margin-expenses.store';
import { MarginNotesStore } from './margin-notes/margin-notes.store';
import { MarginRtoDatesStore } from './margin-rto-dates/margin-rto-dates.store';
import { MarginSetupStore } from './margin-setup/margin-setup.store';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import dayjs from 'dayjs';

type UiState = {
    isSaving: boolean;
    error: string | null;
};

export const LocumsMarginCalculatorStore = signalStore(
    { providedIn: 'root' },

    withState(initialLocumsMarginCalculatorSlice),
    withState<UiState>({ isSaving: false, error: null }),

    withComputed(() => {
        const candidateDetailsStore = inject(CandidateDetailsStore);
        const jobDetailsStore = inject(JobDetailsStore);
        const marginSetupStore = inject(MarginSetupStore);
        const addRatesStore = inject(AdditionalRatesStore);
        const expensesStore = inject(MarginExpensesStore);
        const calcStore = inject(MarginCalculationsStore);
        const notesStore = inject(MarginNotesStore);
        const marginContractStore = inject(MarginContractStore);
        const marginRtoDatesStore = inject(MarginRtoDatesStore);
        const _ldFeatureManager = inject(LDFeatureManager);

        const candidateDetailsSnapshot = computed(() => candidateDetailsStore.getSnapshot());
        const jobDetailsSnapshot = computed(() => jobDetailsStore.getSnapshot());
        const marginSetupSnapshot = computed(() => marginSetupStore.getSnapshot());
        const additionalRatesSnapshot = computed(() => addRatesStore.getSnapshot());
        const expensesSnapshot = computed(() => expensesStore.getSnapshot());
        const calculationsSnapshot = computed(() => calcStore.getSnapshot());
        const notesSnapshot = computed(() => notesStore.getSnapshot());
        const marginContractSnapshot = computed(() => marginContractStore.getSnapshot());
        const marginRtoDatesSnapshot = computed(() => marginRtoDatesStore.getSnapshot());

        const enableSpecialtyPersistenceLocums1099 = toSignal(
            _ldFeatureManager.isEnabled(FeatureFlag.EnableSpecialtyPersistenceLocums1099) ?? of(false),
            { initialValue: false }
        );

        const enableLeave1099GmcDialog = toSignal(
            _ldFeatureManager.isEnabled(FeatureFlag.NovaLocumsLeave1099GmcDialog) ?? of(false),
            { initialValue: false }
        );

        const enableIsFormDirtyWithNormalization = toSignal(
            _ldFeatureManager.isEnabled(FeatureFlag.EnableLocumsMarginIsFormDirtyWithNormalization) ?? of(false),
            { initialValue: false }
        );

        return {
            candidateDetails: candidateDetailsSnapshot,
            jobDetails: jobDetailsSnapshot,
            marginSetup: marginSetupSnapshot,
            additionalRates: additionalRatesSnapshot,
            expenses: expensesSnapshot,
            calculations: calculationsSnapshot,
            notes: notesSnapshot,
            marginContract: marginContractSnapshot,
            marginRtoDates: marginRtoDatesSnapshot,
            enableSpecialtyPersistenceLocums1099,
            enableLeave1099GmcDialog,
            enableIsFormDirtyWithNormalization
        };
    }),

    withMethods((store) => {
        const setupStore = inject(MarginSetupStore);

        const getSnapshot = (): LocumsMarginCalculatorSlice => {
            return {
                candidateDetails: store.candidateDetails(),
                jobDetails: store.jobDetails(),
                marginSetup: store.marginSetup(),
                additionalRates: store.additionalRates(),
                expenses: store.expenses(),
                calculations: store.calculations(),
                notes: store.notes(),
                marginContract: store.marginContract(),
                marginRtoDates: store.marginRtoDates()
            };
        };

        return {
            getSnapshot,

            updateMarginId(id: number) {
                setupStore.setMarginId(id);
            },

            setIsSaving(isSaving: boolean) {
                patchState(store, { isSaving });
            },

            setError(error: string | null) {
                patchState(store, { error });
            },

            isFormDirty(): boolean {
                return isFormDirty(
                    store.initialLoadingData(),
                    getSnapshot(),
                    store.enableSpecialtyPersistenceLocums1099(),
                    store.enableLeave1099GmcDialog(),
                    store.enableIsFormDirtyWithNormalization()
                );
            }
        };
    }),

    withMethods((store) => ({
        setInitialLoadingData: (initialData: Partial<MarginDetails>) => {
            patchState(store, { initialLoadingData: { ...store.initialLoadingData(), ...initialData } });
        }
    })),

    environment.storeWithDevTools('locums-margin-calculator-store')
);

export function isObjectsDifferent(initialValue: any, currentValue: any): boolean {
    if (!initialValue && !currentValue) {
        return false;
    }

    if (Array.isArray(currentValue)) {
        const initialArray = Array.isArray(initialValue) ? initialValue : [];

        if (initialArray.length !== currentValue.length) {
            return true;
        }

        for (let i = 0; i < initialArray.length; i++) {
            if (!isEqual(initialArray[i], currentValue[i])) {
                return true;
            }
        }
        return false;
    }

    return !isEqual(initialValue, currentValue);
}

export function isObjectsDifferentWithNormalization(initialValue: unknown, currentValue: unknown): boolean {
    const normalizedInitialValue = normalizeValues(initialValue);
    const normalizedCurrentValue = normalizeValues(currentValue);

    return !isEqual(normalizedInitialValue, normalizedCurrentValue);
}

function normalizeValues(value: unknown): unknown {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        const dayjsValue = dayjs(value);
        return dayjsValue.isValid() ? dayjsValue.format('YYYY-MM-DDTHH:mm:ss') : null;
    }

    if (typeof value === 'string') {
        return value.trim() === '' ? null : value;
    }

    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value.format('YYYY-MM-DDTHH:mm:ss') : null;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return null;
        }
        return value.map((item: unknown) => normalizeValues(item));
    }

    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).reduce(
            (normalizedObject, [key, objectValue]) => {
                normalizedObject[key] = normalizeValues(objectValue);
                return normalizedObject;
            },
            {} as Record<string, unknown>
        );
    }

    return value;
}

function isFormDirty(
    initialState: MarginDetails,
    currentState: LocumsMarginCalculatorSlice,
    enableSpecialtyPersistenceLocums1099: boolean,
    enableLeave1099GmcDialog: boolean,
    enableIsFormDirtyWithNormalization: boolean
): boolean {
    if (!initialState) {
        return false;
    }

    const sectionsToBeChecked = [
        {
            name: 'Candidate Details',
            initialState: enableSpecialtyPersistenceLocums1099
                ? enableLeave1099GmcDialog
                    ? {
                          expertiseId: initialState.expertiseId ?? 0,
                          expertiseYearsOfExperience: initialState.expertiseYearsOfExperience ?? 0
                      }
                    : {
                          expertiseId: initialState.candidateDetails.expertiseId,
                          expertiseYearsOfExperience: initialState.candidateDetails.expertiseYearsOfExperience
                      }
                : initialState.candidateExpertiseId,
            currentState: enableSpecialtyPersistenceLocums1099
                ? enableLeave1099GmcDialog
                    ? {
                          expertiseId: currentState.candidateDetails.expertiseId ?? 0,
                          expertiseYearsOfExperience: currentState.candidateDetails.expertiseYearsOfExperience ?? 0
                      }
                    : {
                          expertiseId: currentState.candidateDetails.expertiseId,
                          expertiseYearsOfExperience: currentState.candidateDetails.expertiseYearsOfExperience
                      }
                : currentState.candidateDetails.candidateExpertiseId
        },
        {
            name: 'Margin Setup',
            initialState: enableLeave1099GmcDialog
                ? {
                      contractStartDate: initialState.marginSetUpDetails?.contractStartDate ?? null,
                      contractEndDate: initialState.marginSetUpDetails?.contractEndDate ?? null,
                      days: initialState.marginSetUpDetails?.days ?? null,
                      hoursPerShift: initialState.marginSetUpDetails?.hoursPerShift ?? null,
                      billRate: initialState.marginSetUpDetails?.billRate ?? null,
                      payRate: initialState.marginSetUpDetails?.payRate ?? null,
                      overtimeBillRate: initialState.marginSetUpDetails?.overtimeBillRate ?? null,
                      overtimePayRate: initialState.marginSetUpDetails?.overtimePayRate ?? null,
                      overtimeRateFactor: initialState.marginSetUpDetails?.overtimeRateFactor ?? null,
                      holidayBillRate: initialState.marginSetUpDetails?.holidayBillRate ?? null,
                      holidayPayRate: initialState.marginSetUpDetails?.holidayPayRate ?? null,
                      holidayRateFactor: initialState.marginSetUpDetails?.holidayRateFactor ?? null,
                      isAllInclusive: initialState.marginSetUpDetails?.isAllInclusive ?? null
                  }
                : (({ payPeriodTypesId, ...initialMarginSetupState }) => initialMarginSetupState)(
                      initialState.marginSetUpDetails as any
                  ),
            currentState: enableLeave1099GmcDialog
                ? {
                      contractStartDate: currentState.marginSetup.contractStartDate ?? null,
                      contractEndDate: currentState.marginSetup.contractEndDate ?? null,
                      days: currentState.marginSetup.days ?? null,
                      hoursPerShift: currentState.marginSetup.hoursPerShift ?? null,
                      billRate: currentState.marginSetup.billRate ?? null,
                      payRate: currentState.marginSetup.payRate ?? null,
                      overtimeBillRate: currentState.marginSetup.overtimeBillRate ?? null,
                      overtimePayRate: currentState.marginSetup.overtimePayRate ?? null,
                      overtimeRateFactor: currentState.marginSetup.overtimeRateFactor ?? null,
                      holidayBillRate: currentState.marginSetup.holidayBillRate ?? null,
                      holidayPayRate: currentState.marginSetup.holidayPayRate ?? null,
                      holidayRateFactor: currentState.marginSetup.holidayRateFactor ?? null,
                      isAllInclusive: currentState.marginSetup.isAllInclusive ?? null
                  }
                : (({ marginId, marginStatusId, submittalId, ...currentMarginSetupState }) => currentMarginSetupState)(
                      currentState.marginSetup
                  )
        },
        {
            name: 'Additional Rates',
            initialState: initialState.marginAdditionalRates,
            currentState: currentState.additionalRates.additionalRates
        },
        {
            name: 'Expenses',
            initialState: initialState.marginExpenses,
            currentState: currentState.expenses.marginExpenses
        },
        {
            name: 'RTO Dates',
            initialState: initialState.marginRtoDates?.map(({ marginId, rtoDate }) => ({ marginId, rtoDate })),
            currentState: currentState.marginRtoDates.rtoDates?.map(({ marginId, rtoDate }) => ({ marginId, rtoDate }))
        },
        {
            name: 'Notes',
            initialState:
                typeof (initialState.notes as unknown) === 'string'
                    ? (initialState.notes as unknown as string)
                    : (initialState.notes?.marginNotes ?? null),
            currentState: currentState.notes.marginNotes ?? null
        }
    ];

    if (enableIsFormDirtyWithNormalization) {
        for (const sectionToBeChecked of sectionsToBeChecked) {
            if (isObjectsDifferentWithNormalization(sectionToBeChecked.initialState, sectionToBeChecked.currentState)) {
                return true;
            }
        }
    } else {
        for (const sectionToBeChecked of sectionsToBeChecked) {
            if (isObjectsDifferent(sectionToBeChecked.initialState, sectionToBeChecked.currentState)) {
                return true;
            }
        }
    }

    return false;
}
